/**
 * Shared hook for player search pagination with dual-query orchestration.
 *
 * Encapsulates the pattern every player-search surface repeats: dual GraphQL
 * queries (searchPlayers + filterPlayers), dual useInfiniteScroll instances,
 * result unification, client-side text filtering, and loading state
 * derivation.
 * @module hooks/usePlayerSearchPagination
 */

import { useCallback, useEffect, useMemo } from "react";

import {
  OrderByDirection,
  type PlayerAdvancedFilterInput,
  type PlayerSearchPlayerFragment,
  SortField,
  usePlayerSearchFilterPlayersQuery,
  usePlayerSearchSearchPlayersLiteQuery,
  usePlayerSearchSearchPlayersQuery,
} from "@/generated/graphql";

import filterPlayersBySearchText from "@/features/scouting/utils/filterPlayersBySearchText";
import {
  extractCustomMetricId,
  isCustomMetricSort,
} from "@/utils/customMetricsSorting";

import { useInfiniteScroll } from "./useInfiniteScroll";

/** Default page size for search results */
const DEFAULT_PAGE_SIZE = 20;

/**
 * Bounds the client-side text-filter walk (SE-8102). `filterPlayers`'s
 * `PlayerAdvancedFilterInput` has no server-side search parameter, so once a
 * filter AND search text are both active, matching happens entirely
 * client-side against server pages that were never filtered by the term. The
 * auto-load-on-empty-page effect below is the only thing that can locate a
 * match in that case — left unbounded, a zero-match term under a broad
 * filter walks every remaining server page, making real network requests the
 * whole time and holding the "manually add player" affordance (gated on
 * `isEmpty`, which required `hasMore === false`) unreachable for as long as
 * that walk takes.
 *
 * Capped rather than removed: a real match a few pages deep should still
 * surface. 10 pages (200 players at the default page size) bounds the walk
 * to a handful of requests while still covering the common case.
 */
const MAX_AUTO_LOAD_PAGES = 10;

/**
 * Fetch policy for all three player-search documents (SE-7374).
 *
 * Previously `network-only`, which has no cache fallback: every debounce tick
 * cleared `data` to `undefined` for the duration of the round trip, so the
 * results panel blanked to a full-panel spinner on every new term. Typing
 * faster than the backend answers therefore held the panel blank for the whole
 * session, and results only appeared once typing stopped.
 *
 * `cache-and-network` + `nextFetchPolicy: "cache-first"` is what the Pipeline
 * "Add Players" drawer (`useAddPlayersSearch`) already uses, and that surface
 * does not reproduce the stall. A
 * repeated term is then served from cache with no request at all, which also
 * takes load off the query pile-up the ticket describes. `searchPlayers` is
 * key-arg'd on `["query", "sortBy", "sortOrder"]`
 * (`providers/apolloPaginationPolicies.ts`), so each term is its own cache
 * entry and cache-first cannot serve one term's results for another;
 * `filterPlayers` is key-arg'd on its whole `filter` input for the same
 * reason.
 */
const SEARCH_FETCH_POLICY = {
  fetchPolicy: "cache-and-network",
  nextFetchPolicy: "cache-first",
} as const;

/**
 * Picks which query result to DISPLAY: the live one, or the previous one
 * while a new term is in flight (SE-7374).
 *
 * `errorPolicy: "all"` leaves `data` undefined on failure as well as during a
 * fetch, so a failed query must never fall back — showing the previous rows
 * there would present a failure as a stale success.
 * @param data - The active query result, if it has arrived
 * @param previous - The last result for an earlier variable set
 * @param error - The active query error, if any
 * @returns The data to render from, or undefined
 */
const pickDisplayData = <TData>(
  data: TData | undefined,
  previous: TData | undefined,
  error: unknown
): TData | undefined => {
  if (data) return data;
  return error ? undefined : previous;
};

/**
 * Valid backend SortField enum values, for runtime validation before casting.
 * Guards against sending a client-side-only sort key (e.g. "PLAYER_NAME") that
 * the backend rejects, which would empty the results list (SE-5406).
 */
const VALID_SORT_FIELDS: ReadonlySet<string> = new Set(
  Object.values(SortField)
);

/** Configuration for the usePlayerSearchPagination hook */
interface UsePlayerSearchPaginationOptions {
  /** Whether the parent modal is open */
  readonly isOpen: boolean;
  /** Whether the user is authenticated */
  readonly hasAuth: boolean;
  /** Debounced, trimmed, lowercased search text */
  readonly debouncedSearch: string;
  /** Explicit sort field selection (null = no explicit selection, string for custom metrics) */
  readonly sortBy: string | null;
  /** Sort direction */
  readonly sortOrder: OrderByDirection;
  /** Filter variables for the filterPlayers query */
  readonly filterVariables: PlayerAdvancedFilterInput;
  /** Whether any filters are active (drives query routing) */
  readonly hasFilters: boolean;
  /** Page size for pagination (default 20) */
  readonly pageSize?: number;
  /**
   * Whether the caller renders a result count. Defaults to `false`.
   *
   * `totalCount` is expensive: the backend resolves it as
   * `SELECT COUNT(*) FROM (<filter + rank>)` with no LIMIT, so the rank
   * expression is evaluated across every matching row rather than the page's
   * 20. Measured for a 2-character term: 2,066ms for the page query vs 1,446ms
   * for the count — roughly 41% of the operation's SQL time (SE-5776).
   *
   * When false, the text-search path uses the Lite document, which omits the
   * field. `totalCount` is then always `null`, so only opt in if you display it.
   */
  readonly includeTotalCount?: boolean;
}

/** Return value from the usePlayerSearchPagination hook */
interface UsePlayerSearchPaginationReturn {
  /** Unified, filtered player results from the active query */
  readonly players: readonly PlayerSearchPlayerFragment[];
  /** Whether the initial/refetch load is in progress */
  readonly isLoading: boolean;
  /**
   * Whether a new term is in flight while the PREVIOUS term's results are
   * still on screen. The surface shows those results with a lightweight
   * refresh indicator rather than blanking to a full-panel spinner (SE-7374).
   */
  readonly isRefreshing: boolean;
  /** Whether results are empty after a search has been made */
  readonly isEmpty: boolean;
  /** Whether the active query returned an error */
  readonly isError: boolean;
  /** Whether the user has initiated any search/sort/filter action */
  readonly hasSearched: boolean;
  /** Triggers the next page fetch */
  readonly loadMore: () => void;
  /**
   * Re-runs the ACTIVE query from the first page. Exists so a surface can put
   * a working retry on its failure state instead of a dead button — the hook
   * reports `isError` but, without this, gave callers no way to act on it
   * (SE-7640).
   */
  readonly retry: () => void;
  /** Whether there are more pages available */
  readonly hasMore: boolean;
  /** Whether a fetchMore request is currently in progress */
  readonly isLoadingMore: boolean;
  /**
   * Total number of players matching the active query's criteria, from the
   * connection's `totalCount` (SE-5247). `null` when the backend omits it.
   * Note: this is the server-side match count and is NOT reduced by the
   * client-side text filter applied to `players`.
   */
  readonly totalCount: number | null;
  /**
   * Whether `isEmpty` is true because the auto-load walk (SE-8102's
   * `MAX_AUTO_LOAD_PAGES`) gave up while the server still reports more pages
   * — NOT because the search genuinely has no matches (SE-8324).
   *
   * `isEmpty` alone cannot distinguish these: its `(!hasMore ||
   * autoLoadBudgetExhausted)` clause goes true for both a real end-of-results
   * and a budget-cut walk, so a name+filter combination whose match happens
   * to sit past page 10 reads identically to a genuine zero-match search.
   * Callers should surface a distinct "we stopped searching" message rather
   * than a confident "no players found" when this is true.
   */
  readonly isEmptyDueToAutoLoadBudget: boolean;
}

/**
 * Orchestrates dual search/filter queries with infinite scroll pagination.
 *
 * Routes to `searchPlayers` when no filters are active, and to `filterPlayers`
 * when filters are present. Applies client-side text filtering on filter query
 * results since the backend's PlayerAdvancedFilterInput has no search field.
 * @param options - Search, sort, filter, and pagination configuration
 * @param options.isOpen - Whether the parent modal is open
 * @param options.hasAuth - Whether the user is authenticated
 * @param options.debouncedSearch - Debounced, trimmed, lowercased search text
 * @param options.sortBy - Explicit sort field selection (null = no explicit selection)
 * @param options.sortOrder - Sort direction
 * @param options.filterVariables - Filter variables for the filterPlayers query
 * @param options.hasFilters - Whether any filters are active (drives query routing)
 * @param options.pageSize - Page size for pagination (default 20)
 * @param options.includeTotalCount - Whether the caller renders a result count
 *   (default false). When false the text-search path uses the Lite document,
 *   which omits `totalCount` and its unbounded server-side COUNT(*) (SE-5776).
 * @returns Unified player results with loading/pagination state
 */
export const usePlayerSearchPagination = ({
  isOpen,
  hasAuth,
  debouncedSearch,
  sortBy,
  sortOrder,
  filterVariables,
  hasFilters,
  pageSize = DEFAULT_PAGE_SIZE,
  includeTotalCount = false,
}: UsePlayerSearchPaginationOptions): UsePlayerSearchPaginationReturn => {
  /* --- Always send sort so the backend returns GPR-sorted results on open --- */
  const sortVariables = useMemo(() => {
    const effective = sortBy ?? SortField.Gpr;
    if (isCustomMetricSort(effective)) {
      return {
        sortBy: SortField.CustomStat,
        sortByCustomStatName: extractCustomMetricId(effective),
        sortOrder,
      };
    }
    // Guard: only forward values that are real SortField enum members. An
    // `as SortField` cast on an arbitrary string (e.g. the client-side-only
    // "PLAYER_NAME") would send an invalid enum to the backend, which rejects
    // it and returns an empty list (SE-5406). Fall back to GPR instead.
    //
    // When the field is invalid (a client-only sort like PLAYER_NAME), pin the
    // direction too. Otherwise the caller's asc/desc toggle would change which
    // backend page gets fetched, so the client re-order would operate on a
    // different slice each way instead of one stable GPR page.
    const isValidSortField = (VALID_SORT_FIELDS as Set<string>).has(effective);
    return isValidSortField
      ? { sortBy: effective as SortField, sortOrder }
      : { sortBy: SortField.Gpr, sortOrder: OrderByDirection.Desc };
  }, [sortBy, sortOrder]);

  /* --- Text-only search query (no filters active) ---
   * Two documents, identical apart from `totalCount`. Exactly one runs: hooks
   * cannot be called conditionally, so the unused one is skipped rather than
   * omitted. Callers that do not render a count get the Lite document and skip
   * the unbounded COUNT(*) the field costs (SE-5776). */
  const searchVariables = {
    query: debouncedSearch,
    first: pageSize,
    ...sortVariables,
  };
  const searchSkip = !isOpen || !hasAuth || hasFilters;

  const fullSearch = usePlayerSearchSearchPlayersQuery({
    variables: searchVariables,
    skip: searchSkip || !includeTotalCount,
    ...SEARCH_FETCH_POLICY,
    notifyOnNetworkStatusChange: true,
  });

  const liteSearch = usePlayerSearchSearchPlayersLiteQuery({
    variables: searchVariables,
    skip: searchSkip || includeTotalCount,
    ...SEARCH_FETCH_POLICY,
    notifyOnNetworkStatusChange: true,
  });

  /* Whichever of the two search documents is the live one. Selected once, as a
   * whole result, rather than field by field: six parallel
   * `includeTotalCount ? full : lite` reads were six chances to route one field
   * to the skipped document. `fullSearch.data` is still read directly below for
   * `totalCount`, which only the counting document selects. */
  const activeSearch = includeTotalCount ? fullSearch : liteSearch;
  const {
    data: searchData,
    previousData: searchPreviousData,
    error: searchError,
    fetchMore: fetchMoreSearch,
    refetch: refetchSearch,
    networkStatus: searchNetworkStatus,
  } = activeSearch;

  /* --- Filter query (when filters are active) --- */
  const {
    data: filterData,
    previousData: filterPreviousData,
    error: filterError,
    fetchMore: fetchMoreFilter,
    refetch: refetchFilter,
    networkStatus: filterNetworkStatus,
  } = usePlayerSearchFilterPlayersQuery({
    variables: {
      filter: filterVariables,
      first: pageSize,
      ...sortVariables,
    },
    skip: !isOpen || !hasAuth || !hasFilters,
    ...SEARCH_FETCH_POLICY,
    notifyOnNetworkStatusChange: true,
  });

  /* --- Infinite scroll for both queries --- */
  const {
    loadMore: searchLoadMore,
    isLoadingMore: searchIsLoadingMore,
    hasMore: searchHasMore,
  } = useInfiniteScroll({
    pageInfo: searchData?.searchPlayers?.pageInfo,
    fetchMore: fetchMoreSearch,
    networkStatus: searchNetworkStatus,
  });

  const {
    loadMore: filterLoadMore,
    isLoadingMore: filterIsLoadingMore,
    hasMore: filterHasMore,
  } = useInfiniteScroll({
    pageInfo: filterData?.filterPlayers?.pageInfo,
    fetchMore: fetchMoreFilter,
    networkStatus: filterNetworkStatus,
  });

  /* --- Results shown while a new term is still in flight (SE-7374) ---
   * `data` is `undefined` for the whole round trip of a term the cache has not
   * seen, so falling back to `previousData` keeps the last answered term's rows
   * on screen instead of blanking the panel. Suppressed when the active query
   * FAILED: `errorPolicy: "all"` leaves `data` undefined on failure too, and
   * showing stale rows there would present a failure as a stale success. */
  const searchDataForDisplay = pickDisplayData(
    searchData,
    searchPreviousData,
    searchError
  );
  const filterDataForDisplay = pickDisplayData(
    filterData,
    filterPreviousData,
    filterError
  );

  /* --- Unify results from whichever query is active --- */
  const players = useMemo((): readonly PlayerSearchPlayerFragment[] => {
    const raw = hasFilters
      ? ((filterDataForDisplay?.filterPlayers?.edges?.map(e => e.node) ??
          []) as PlayerSearchPlayerFragment[])
      : ((searchDataForDisplay?.searchPlayers?.edges?.map(e => e.node) ??
          []) as PlayerSearchPlayerFragment[]);

    // Client-side text filtering when filterPlayers query is active
    // (backend's PlayerAdvancedFilterInput has no search field)
    return hasFilters ? filterPlayersBySearchText(raw, debouncedSearch) : raw;
  }, [hasFilters, filterDataForDisplay, searchDataForDisplay, debouncedSearch]);

  /* --- Derived state --- */
  const hasSearched = isOpen;
  const activeNetworkStatus = hasFilters
    ? filterNetworkStatus
    : searchNetworkStatus;
  const isLoading =
    hasSearched &&
    (activeNetworkStatus === 1 ||
      activeNetworkStatus === 2 ||
      activeNetworkStatus === 4);
  const loadMore = hasFilters ? filterLoadMore : searchLoadMore;
  const isLoadingMore = hasFilters ? filterIsLoadingMore : searchIsLoadingMore;
  const hasMore = hasFilters ? filterHasMore : searchHasMore;
  const isError = Boolean(hasFilters ? filterError : searchError);
  /* Re-runs the active query from page one, so a failure state can carry a
   * retry the user can actually press (SE-7640). The rejection is swallowed
   * deliberately: a failed retry is already reported through `isError`, and an
   * unhandled rejection here would surface as a crash instead. */
  const activeRefetch = hasFilters ? refetchFilter : refetchSearch;
  const retry = useCallback(() => {
    void activeRefetch().catch(() => undefined);
  }, [activeRefetch]);

  /* --- Auto-load walk budget (SE-8102) ---
   * Derived from the filterPlayers connection's own accumulated edge count —
   * no ref/state counter needed. `filterDataForDisplay` already grows by one
   * server page per `loadMore()` (Apollo merges each fetchMore page into the
   * same cached connection), so it is both the walk's progress AND the
   * budget check, read the normal (render-safe) way. Because the connection
   * is keyed on `filter` alone — not on `debouncedSearch`, which has no
   * server-side counterpart — switching search terms under the same filter
   * reuses whatever was already fetched instead of re-walking from zero. */
  const fetchedFilterPlayerCount =
    filterDataForDisplay?.filterPlayers?.edges?.length ?? 0;
  const autoLoadBudgetExhausted =
    fetchedFilterPlayerCount >= MAX_AUTO_LOAD_PAGES * pageSize;

  const isEmpty =
    !isLoading &&
    players.length === 0 &&
    hasSearched &&
    (!hasMore || autoLoadBudgetExhausted);
  /*
   * Distinguishes a budget-cut walk from a genuine end-of-results (SE-8324).
   * `hasMore` is still true here ONLY when the budget — not the server — is
   * what stopped the search; when the budget was exhausted on exactly the
   * last page, `hasMore` is false and the result is a real zero-match, so
   * that case is deliberately excluded.
   */
  const isEmptyDueToAutoLoadBudget =
    isEmpty && autoLoadBudgetExhausted && hasMore;
  /* A new term is in flight while the previous term's rows are still shown.
   * Distinct from `isLoading` with nothing to show (full-panel spinner) and
   * from `isLoadingMore` (pagination footer). */
  const isRefreshing = isLoading && players.length > 0;
  // Null on the text-search path unless the caller opted in: the Lite document
  // does not select the field.
  const totalCount =
    (hasFilters
      ? filterData?.filterPlayers?.totalCount
      : fullSearch.data?.searchPlayers?.totalCount) ?? null;

  /* --- Auto-fetch next page when client-side text filter empties current results ---
   * When filters + search text are active, the backend returns unfiltered pages
   * and we filter client-side. If the current pages yield zero matches but more
   * server pages exist, the scroll view is empty and the user cannot trigger
   * loadMore via scrolling. This effect bridges that gap automatically —
   * bounded by MAX_AUTO_LOAD_PAGES (SE-8102) so a zero-match term under a
   * broad filter can't walk the entire filtered set. */
  useEffect(() => {
    if (
      hasFilters &&
      debouncedSearch &&
      players.length === 0 &&
      hasMore &&
      !isLoading &&
      !isLoadingMore &&
      !autoLoadBudgetExhausted
    ) {
      loadMore();
    }
  }, [
    hasFilters,
    debouncedSearch,
    players.length,
    hasMore,
    isLoading,
    isLoadingMore,
    autoLoadBudgetExhausted,
    loadMore,
  ]);

  return useMemo(
    () => ({
      players,
      isLoading,
      isRefreshing,
      isEmpty,
      isError,
      hasSearched,
      loadMore,
      retry,
      hasMore,
      isLoadingMore,
      totalCount,
      isEmptyDueToAutoLoadBudget,
    }),
    [
      players,
      isLoading,
      isRefreshing,
      isEmpty,
      isError,
      hasSearched,
      loadMore,
      retry,
      hasMore,
      isLoadingMore,
      totalCount,
      isEmptyDueToAutoLoadBudget,
    ]
  );
};
